import { TriggerType } from '@pushly/models/lib/enums'
import { JourneyType } from '@pushly/models/lib/enums/journeys'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import {
    JOURNEY_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS,
    JOURNEY_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD,
    JOURNEY_DEFAULT_ABANDONMENT_METRIC,
    JOURNEY_DEFAULT_ABANDONMENT_SECONDS,
} from '../../../components/journey-builder/configuration-editor/constants'
import type { Journey, JourneyConfiguration } from '../types/journey-api-response'

/**
 * Returns a copy of the journey with the abandonment settings its trigger type implies: defaults
 * filled in for abandoned cart and abandoned view triggers, and the settings removed for every
 * other trigger type.
 *
 * Apply it both to the journey being edited and to the saved journey it is compared against, so a
 * journey saved without these settings does not appear to have unsaved changes the moment it is
 * opened.
 */
export function applyJourneyAbandonmentDefaults(journey: Journey): Journey {
    const updated = structuredClone(journey)
    const config = (updated.configuration ??= {} as JourneyConfiguration)
    const triggerType = extractJourneyTrigger(updated)?.configuration?.type
    const isAbandonedCartTrigger = triggerType === TriggerType.ABANDONED_CART
    const isAbandonedViewTrigger = triggerType === TriggerType.ABANDONED_VIEW

    if (!isAbandonedCartTrigger && !isAbandonedViewTrigger) {
        config.abandonment_seconds = undefined
        config.abandonment_metric = undefined
        config.abandoned_cart_cooldown_period = undefined
        config.abandoned_browse_minimum_item_views = undefined
        return updated
    }

    if (config.abandonment_seconds === undefined) {
        config.abandonment_seconds = JOURNEY_DEFAULT_ABANDONMENT_SECONDS
        config.abandonment_metric = JOURNEY_DEFAULT_ABANDONMENT_METRIC
    }

    if (isAbandonedViewTrigger) {
        if (config.abandoned_cart_cooldown_period?.interval_seconds === undefined) {
            config.abandoned_cart_cooldown_period = {
                interval_seconds: JOURNEY_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD.interval_seconds,
            }
        }

        if (config.abandoned_browse_minimum_item_views === undefined) {
            config.abandoned_browse_minimum_item_views = JOURNEY_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS
        }
    }

    return updated
}

/**
 * Returns a copy of a triggered journey that has no audience segments, targeting the domain's
 * default segment instead. Any other journey is returned unchanged.
 *
 * The API rejects an audience with an empty segment list, so a triggered journey whose segments
 * were never chosen needs one before it can be saved. Targeting the default segment reaches the
 * same subscribers as targeting no segment at all.
 *
 * Apply this once, when a journey is loaded, rather than on every change: a user choosing
 * specific segments starts from an empty list, and re-applying the default would undo that choice.
 */
export function applyJourneyDefaultAudienceSegment(
    journey: Journey,
    domainDefaultSegmentId: number | undefined,
): Journey {
    const segmentIds = journey.configuration?.audience?.segment_ids
    const hasSegments = Array.isArray(segmentIds) && segmentIds.length > 0

    if (domainDefaultSegmentId === undefined || journey.type !== JourneyType.TRIGGER || hasSegments) {
        return journey
    }

    const updated = structuredClone(journey)
    const config = (updated.configuration ??= {} as JourneyConfiguration)
    config.audience = config.audience ?? {}
    config.audience.segment_ids = [domainDefaultSegmentId]

    return updated
}
